import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { useCallback, useRef, useState } from 'react';

/**
 * Returns React state plus a ref that updates immediately when the setter runs.
 */
export function useStateWithRef<T>(
  initialState: T | (() => T),
): [T, Dispatch<SetStateAction<T>>, MutableRefObject<T>] {
  const [state, setState] = useState(initialState);
  const stateRef = useRef(state);

  const setStateWithRef = useCallback<Dispatch<SetStateAction<T>>>((nextState) => {
    const resolvedState =
      typeof nextState === 'function'
        ? (nextState as (previous: T) => T)(stateRef.current)
        : nextState;

    stateRef.current = resolvedState;
    setState(resolvedState);
  }, []);

  return [state, setStateWithRef, stateRef];
}
